/**
 * Auth Audit Logger
 * Logs unauthorized access attempts and authentication events
 */

export interface AuthAuditEvent {
  type: 'unauthorized_access' | 'role_check' | 'login_attempt' | 'logout';
  timestamp: string;
  userId?: string;
  email?: string;
  ipAddress?: string;
  userAgent?: string;
  attemptedPath?: string;
  requiredRole?: string;
  userRole?: string;
  outcome: 'allowed' | 'denied';
}

class AuthAuditLogger {
  private logs: AuthAuditEvent[] = [];

  /**
   * Log unauthorized access attempt
   */
  logUnauthorizedAccess(params: {
    userId?: string;
    email?: string;
    attemptedPath: string;
    requiredRole: string;
    userRole?: string;
  }): void {
    const event: AuthAuditEvent = {
      type: 'unauthorized_access',
      timestamp: new Date().toISOString(),
      attemptedPath: params.attemptedPath,
      requiredRole: params.requiredRole,
      outcome: 'denied'
    };

    // Only add defined optional properties
    if (params.userId !== undefined) event.userId = params.userId;
    if (params.email !== undefined) event.email = params.email;
    if (params.userRole !== undefined) event.userRole = params.userRole;

    this.logs.push(event);

    // In production, send to server/API
    if (import.meta.env.MODE === 'production') {
      this.sendToAuditLog(event);
    } else {
      // Development: log to console
      console.warn('🚫 Unauthorized Access Attempt:', event);
    }
  }

  /**
   * Log role check
   */
  logRoleCheck(params: {
    userId: string;
    email: string;
    requiredRole: string;
    userRole: string;
    outcome: 'allowed' | 'denied';
  }): void {
    const event: AuthAuditEvent = {
      type: 'role_check',
      timestamp: new Date().toISOString(),
      requiredRole: params.requiredRole,
      userRole: params.userRole,
      outcome: params.outcome
    };

    // Only add defined optional properties
    if (params.userId !== undefined) event.userId = params.userId;
    if (params.email !== undefined) event.email = params.email;

    this.logs.push(event);

    if (import.meta.env.MODE === 'production') {
      this.sendToAuditLog(event);
    }
  }

  /**
   * Get all audit logs (for debugging/admin viewing)
   */
  getLogs(): AuthAuditEvent[] {
    return [...this.logs];
  }

  /**
   * Get logs by type
   */
  getLogsByType(type: AuthAuditEvent['type']): AuthAuditEvent[] {
    return this.logs.filter(log => log.type === type);
  }

  /**
   * Clear logs (for testing/debugging)
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Send audit event to server/API
   * In production, this would POST to a logging endpoint
   */
  private sendToAuditLog(event: AuthAuditEvent): void {
    // TODO: Implement API call to audit log endpoint
    // For now, store in localStorage for persistence
    try {
      const existingLogs = JSON.parse(localStorage.getItem('authAuditLogs') || '[]');
      existingLogs.push(event);

      // Keep only last 1000 logs to prevent storage overflow
      const trimmedLogs = existingLogs.slice(-1000);

      localStorage.setItem('authAuditLogs', JSON.stringify(trimmedLogs));
    } catch (error) {
      console.error('Failed to save audit log:', error);
    }
  }

  /**
   * Load persisted logs from localStorage
   */
  loadPersistedLogs(): void {
    try {
      const stored = localStorage.getItem('authAuditLogs');
      if (stored) {
        const parsedLogs = JSON.parse(stored) as AuthAuditEvent[];
        this.logs = [...this.logs, ...parsedLogs];
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    }
  }
}

// Singleton instance
export const authAuditLogger = new AuthAuditLogger();

// Load persisted logs on initialization
if (typeof window !== 'undefined') {
  authAuditLogger.loadPersistedLogs();
}
